import * as React from "react";
import {Chip} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {useTranslation} from "react-i18next";

export function isApprovedStatus(status: string | null | undefined): boolean {
    return (status ?? "").toUpperCase() === "APPROVED";
}

export function isRejectedStatus(status: string | null | undefined): boolean {
    return (status ?? "").toUpperCase() === "REJECTED";
}

/** SUBMITTED = "Submit for Approval" was done in Work Evidence, so it can now be approved/rejected. */
export function isSubmittedStatus(status: string | null | undefined): boolean {
    return (status ?? "").toUpperCase() === "SUBMITTED";
}

export type EvidenceState = "IN_PREPARATION" | "SUBMITTED" | "APPROVED";

/** Single classification used by every view; mirrors the backend summary buckets (rejected/missing = in preparation). */
export function getEvidenceState(status: string | null | undefined): EvidenceState {
    if (isApprovedStatus(status)) return "APPROVED";
    if (isSubmittedStatus(status)) return "SUBMITTED";
    return "IN_PREPARATION";
}

interface StatusChipProps {
    status: string | null | undefined;
    size?: "small" | "medium";
}

/** Renders a work-evidence status as a chip: approved (green), rejected (red), otherwise "in preparation" (orange). */
export default function StatusChip({status, size = "small"}: StatusChipProps): React.ReactElement {
    const {t} = useTranslation();

    if (isApprovedStatus(status)) {
        return (
            <Chip
                icon={<CheckCircleIcon/>}
                label={t('workEvidence.status.approved')}
                color="success"
                size={size}
                sx={{fontWeight: 600}}
            />
        );
    }

    if (isRejectedStatus(status)) {
        return (
            <Chip
                icon={<CancelIcon/>}
                label={t('workEvidence.status.rejected')}
                color="error"
                size={size}
                sx={{fontWeight: 600}}
            />
        );
    }

    if (isSubmittedStatus(status)) {
        return (
            <Chip
                icon={<AccessTimeIcon/>}
                label={t('workEvidence.status.submitted')}
                color="info"
                size={size}
                sx={{fontWeight: 600}}
            />
        );
    }

    return (
        <Chip
            icon={<AccessTimeIcon/>}
            label={t('workEvidence.status.pending')}
            size={size}
            sx={{
                fontWeight: 600,
                backgroundColor: "warning.light",
                color: "warning.dark",
                "& .MuiChip-icon": {color: "warning.dark"},
            }}
        />
    );
}
